/**
 * Admin-only pipeline spreadsheet round trip (Block Two).
 * Download the live pipeline as Excel, work in it, upload it back. Nothing applies until the
 * admin confirms a field-by-field preview. Lives beside the client list download because
 * commercial data is not downloadable by everyone.
 */
import { useRef, useState } from "react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Download, Upload, Loader2, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  opportunity_name: "Deal name", stage: "Stage", status: "Status",
  opportunity_value: "Value", opportunity_currency: "Currency",
  expected_close_date: "Expected close", deal_owner_name: "Owner",
  account_manager_name: "Account manager", notes: "Notes",
};

export default function PipelineRoundTrip() {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);     // preview plan awaiting confirmation
  const [pendingFile, setPendingFile] = useState(null);
  const [errors, setErrors] = useState(null);

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/opportunities/export/excel`, { credentials: "include" });
      if (!res.ok) { toast.error(res.status === 403 ? "Admin only" : "Export failed"); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] || "rex_pipeline.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Pipeline downloaded");
    } catch { toast.error("Export failed"); } finally { setBusy(false); }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErrors(null); setPlan(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/opportunities/import/excel?apply=false`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setErrors(data?.detail?.errors || [data?.detail?.message || "Upload rejected"]);
      } else {
        setPendingFile(file); setPlan(data);
      }
    } catch { toast.error("Upload failed"); } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const apply = async () => {
    if (!pendingFile) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", pendingFile);
      const res = await fetch(`${API}/opportunities/import/excel?apply=true`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json();
      if (!res.ok) { setErrors(data?.detail?.errors || ["Apply failed"]); setPlan(null); return; }
      toast.success(`Applied ${data.changes_applied} change(s), created ${data.created.length}, ${data.unchanged_count} unchanged`);
      setPlan(null); setPendingFile(null);
    } catch { toast.error("Apply failed"); } finally { setBusy(false); }
  };

  const close = () => { setPlan(null); setPendingFile(null); setErrors(null); };

  return (
    <>
      <Button variant="outline" size="sm" onClick={download} disabled={busy} data-testid="pipeline-download-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
        Pipeline (Excel)
      </Button>
      <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="pipeline-upload-btn">
        <Upload className="w-4 h-4 mr-1" /> Upload pipeline
      </Button>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onPick} data-testid="pipeline-file-input" />

      {/* Validation errors — all or nothing, nothing applied */}
      <Dialog open={!!errors} onOpenChange={(o) => !o && setErrors(null)}>
        <DialogContent data-testid="pipeline-errors-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Upload rejected — nothing was changed
            </DialogTitle>
            <DialogDescription>Fix these rows in the spreadsheet and upload again.</DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-red-700 list-disc list-inside space-y-1 max-h-72 overflow-auto">
            {(errors || []).map((e, i) => <li key={i} data-testid={`pipeline-error-${i}`}>{e}</li>)}
          </ul>
          <DialogFooter><Button variant="outline" onClick={() => setErrors(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation — field-by-field preview before anything is written */}
      <Dialog open={!!plan} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl" data-testid="pipeline-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Review pipeline changes before applying</DialogTitle>
            <DialogDescription>
              {plan?.change_count || 0} edit(s), {plan?.creation_count || 0} new deal(s), {plan?.unchanged_count || 0} unchanged. Nothing is written until you apply.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[50vh] overflow-auto space-y-3 text-sm">
            {plan?.changes?.length > 0 && (
              <div data-testid="pipeline-confirm-changes">
                <p className="font-semibold text-slate-700 mb-1">Edits</p>
                {plan.changes.map((c) => (
                  <div key={c.opportunity_id} className="border rounded p-2 mb-1.5" data-testid={`pipeline-change-${c.opportunity_id}`}>
                    <p className="font-medium">{c.company_name} — {c.deal_name}</p>
                    {c.fields.map((f, i) => (
                      <p key={i} className="text-xs text-slate-600">
                        {FIELD_LABELS[f.field] || f.field}: <span className="text-red-600 line-through">{String(f.old)}</span>{" → "}
                        <span className="text-emerald-700 font-medium">{String(f.new)}</span>
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {plan?.creations?.length > 0 && (
              <div data-testid="pipeline-confirm-creations">
                <p className="font-semibold text-slate-700 mb-1">New deals</p>
                {plan.creations.map((cr, i) => (
                  <div key={i} className="border rounded p-2 mb-1.5 bg-emerald-50" data-testid={`pipeline-creation-${i}`}>
                    <p className="font-medium">{cr.company_name} — {cr.fields.opportunity_name}</p>
                    <p className="text-xs text-slate-600">
                      {cr.fields.stage || "First Contact Made"}
                      {cr.fields.opportunity_value ? ` · ${cr.fields.opportunity_currency || "NGN"} ${Number(cr.fields.opportunity_value).toLocaleString()}` : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {(!plan?.changes?.length && !plan?.creations?.length) && (
              <p className="text-slate-500" data-testid="pipeline-nochanges">No edits or new deals — every row matches what is stored.</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={close} disabled={busy} data-testid="pipeline-cancel-btn">Cancel</Button>
            <Button onClick={apply} disabled={busy || (!plan?.change_count && !plan?.creation_count)} data-testid="pipeline-apply-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
              Apply changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
